/**
 * PublicTrackingPage
 * ==================
 * Unauthenticated, shareable tracking viewer. Reached via `/track/:token` —
 * the token is generated by ops/admin from inside the app and shared with the
 * end client or trucker. Designed mobile-first since most viewers will open
 * the link from a phone.
 *
 * Privacy: shows shipment progress (status, ETAs, container list, public
 * timeline events) but never costs, internal notes, supplier or client names.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicTracking } from '../lib/api';
import {
  Ship, MapPin, Anchor, Truck, Package, Clock, CheckCircle2,
  AlertTriangle, Calendar, Loader2,
} from 'lucide-react';
import { LanguagePicker } from '../components/LanguagePicker';

// Map status_code → ordered position in the journey so we can paint a
// horizontal progress bar at the top of the page.
const JOURNEY_STAGES = [
  { code: 'NO_SALIDO_CHINA',         label: 'En origen',     icon: Anchor },
  { code: 'EN_TRANSITO_MARITIMO',    label: 'Tránsito marítimo', icon: Ship },
  { code: 'EN_TRANSITO_PUERTO',      label: 'En puerto',     icon: Anchor },
  { code: 'EN_PANTACO',              label: 'Pantaco',       icon: Package },
  { code: 'EN_TRANSITO_TERRESTRE',   label: 'En ruta',       icon: Truck },
  { code: 'EN_DESTINO',              label: 'En destino',    icon: MapPin },
  { code: 'ENTREGADO',               label: 'Entregado',     icon: CheckCircle2 },
];

// Container-level statuses we also collapse onto the journey bar.
const CONTAINER_STATUS_TO_STAGE = {
  NO_SALIDO_CHINA: 0,
  EN_TRANSITO_MARITIMO: 1,
  ARRIBADO_PUERTO: 2,
  EN_PUERTO_LIBERACION: 2,
  EN_PUERTO_LIBERADO: 2,
  EN_PUERTO_SEMAFORO_ROJO: 2,
  EN_PUERTO_CARGA_FERRO: 2,
  EN_TRANSITO_TERRESTRE: 4,
  EN_PANTACO: 3,
  EN_DESTINO: 5,
  ENTREGADO: 6,
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

function StatusBadge({ code, label }) {
  // Map code → color class. Anything terminal (ENTREGADO) gets green.
  const cls = code === 'ENTREGADO'
    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
    : code === 'EN_DESTINO'
    ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
    : code === 'EN_TRANSITO_TERRESTRE' || code === 'EN_PANTACO'
    ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
    : 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40';
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${cls}`}
      data-testid="public-track-status-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      {label || code || '—'}
    </span>
  );
}

function JourneyBar({ stageIndex }) {
  // Horizontal stepper. Filled up to stageIndex.
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <div className="flex items-center gap-2 min-w-[640px]">
        {JOURNEY_STAGES.map((stage, i) => {
          const Icon = stage.icon;
          const done = i <= stageIndex;
          const active = i === stageIndex;
          return (
            <React.Fragment key={stage.code}>
              <div
                className="flex flex-col items-center min-w-[72px]"
                data-testid={`public-track-stage-${stage.code}`}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors ${
                    done
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'bg-card border-border text-muted-foreground'
                  } ${active ? 'ring-4 ring-primary/20' : ''}`}
                >
                  <Icon size={16} />
                </div>
                <span className={`mt-2 text-[10px] uppercase tracking-wide text-center ${
                  done ? 'text-foreground font-medium' : 'text-muted-foreground'
                }`}>{stage.label}</span>
              </div>
              {i < JOURNEY_STAGES.length - 1 && (
                <div className={`flex-1 h-0.5 ${i < stageIndex ? 'bg-primary' : 'bg-border'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1.5">
        <Icon size={14} />
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-sm font-medium" data-testid={`public-track-info-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {value || '—'}
      </div>
    </div>
  );
}

function ContainerCard({ container }) {
  const stageIdx = CONTAINER_STATUS_TO_STAGE[container.status_code] ?? 0;
  const isDelivered = container.status_code === 'ENTREGADO' || !!container.delivered_at;
  return (
    <div
      className="bg-card border border-border rounded-lg p-4 hover:border-primary/40 transition-colors"
      data-testid={`public-track-container-${container.container_number}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Package size={16} className="text-muted-foreground" />
            <span className="font-mono font-semibold text-base">{container.container_number}</span>
          </div>
          {container.carrier_name && (
            <div className="text-xs text-muted-foreground mt-1">
              Naviera: <span className="font-medium" style={{ color: container.carrier_color || undefined }}>{container.carrier_name}</span>
            </div>
          )}
        </div>
        <StatusBadge code={container.status_code} label={container.status_label} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-muted-foreground">Arribo</div>
          <div className="font-medium">{formatDate(container.arrived_at)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">ETA entrega</div>
          <div className="font-medium">{formatDate(container.eta_entrega_estimada)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Entregado</div>
          <div className={`font-medium ${isDelivered ? 'text-emerald-400' : ''}`}>
            {formatDate(container.delivered_at)}
          </div>
        </div>
      </div>
      {/* Mini-stepper of stage progress */}
      <div className="mt-3 flex gap-1">
        {JOURNEY_STAGES.map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full ${i <= stageIdx ? 'bg-primary' : 'bg-border'}`}
          />
        ))}
      </div>
    </div>
  );
}

// One event in the vertical timeline. Strips the trailing "| key" technical
// suffix that our scrapers append to event notes — that's an internal
// dedup marker, not useful to end clients.
function TimelineEntry({ event, isLast }) {
  const cleanNote = (event.note || '').split(' | ')[0];
  return (
    <div className="flex gap-3" data-testid={`public-track-event`}>
      <div className="flex flex-col items-center">
        <div className="w-2.5 h-2.5 rounded-full bg-primary mt-1.5" />
        {!isLast && <div className="w-px flex-1 bg-border mt-1" />}
      </div>
      <div className="flex-1 pb-5">
        <div className="text-xs text-muted-foreground">{formatDateTime(event.event_date)}</div>
        <div className="text-sm font-medium mt-0.5">{cleanNote || event.event_type_label || event.event_type_code}</div>
        {event.container_number && (
          <div className="text-xs font-mono text-muted-foreground mt-0.5">{event.container_number}</div>
        )}
      </div>
    </div>
  );
}

export default function PublicTrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchPublicTracking(token)
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => {
        const status = e?.response?.status;
        if (mounted) setError(status === 404 ? 'no_disponible' : 'error');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="public-track-loading">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4 bg-card border border-border rounded-lg p-8" data-testid="public-track-not-available">
          <AlertTriangle size={48} className="mx-auto text-amber-400" />
          <h1 className="text-2xl font-heading">Link no disponible</h1>
          <p className="text-muted-foreground text-sm">
            Este link de seguimiento es inválido o ha expirado.
            Pide a tu contacto de OFIMEX un link nuevo.
          </p>
          <a
            href="https://myofimex.com"
            className="inline-block mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-colors"
          >
            Ir a OFIMEX
          </a>
        </div>
      </div>
    );
  }

  const stageIndex = JOURNEY_STAGES.findIndex(s => s.code === data.status_code);
  const effectiveStageIndex = stageIndex === -1 ? 0 : stageIndex;

  return (
    <div className="min-h-screen bg-background" data-testid="public-tracking-page">
      {/* Header */}
      <header className="border-b border-border bg-card/40 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo-ofimex-white.png"
              alt="OFIMEX"
              className="h-8 w-auto object-contain"
            />
            <div className="hidden sm:block border-l border-border pl-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Seguimiento</div>
              <div className="text-sm font-medium">Embarque en tránsito</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-3" data-testid="public-track-last-update">
            <span className="hidden sm:inline-flex items-center">
              <Clock size={12} className="inline mr-1" />
              Actualizado: {formatDateTime(data.last_update_at)}
            </span>
            <span className="sm:hidden">
              <Clock size={12} className="inline mr-1" />
              {formatDateTime(data.last_update_at)}
            </span>
            <LanguagePicker />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-10 space-y-8">
        {/* Hero */}
        <section className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">B/L (Bill of Lading)</div>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold font-mono break-all" data-testid="public-track-mbl">
                {data.mbl}
              </h1>
              {data.vessel_name && (
                <div className="text-sm text-muted-foreground mt-1">Buque: <span className="font-medium">{data.vessel_name}</span></div>
              )}
            </div>
            <StatusBadge code={data.status_code} label={data.status_label} />
          </div>

          {/* Journey bar */}
          <div className="bg-card border border-border rounded-lg p-5">
            <JourneyBar stageIndex={effectiveStageIndex} />
          </div>
        </section>

        {/* Info grid */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <InfoTile icon={Anchor} label="Origen" value={data.origin} />
          <InfoTile icon={MapPin} label="Destino" value={data.destination} />
          <InfoTile icon={Ship} label="Puerto" value={data.puerto} />
          <InfoTile
            icon={Calendar}
            label="ETA entrega"
            value={data.delivered_at ? `Entregado ${formatDate(data.delivered_at)}` : formatDate(data.eta_entrega_estimada || data.eta_final)}
          />
        </section>

        {/* ETAs detail */}
        <section className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <InfoTile icon={Ship} label="ETA naviera" value={formatDate(data.eta_naviera)} />
          <InfoTile icon={Anchor} label="Arribo a puerto" value={formatDate(data.eta_real || data.eta_port)} />
          <InfoTile icon={Truck} label="ETA entrega" value={formatDate(data.eta_entrega_estimada)} />
        </section>

        {/* Containers */}
        <section className="space-y-3">
          <h2 className="text-lg font-heading flex items-center gap-2">
            <Package size={18} />
            Contenedores ({data.containers?.length || 0})
          </h2>
          {(!data.containers || data.containers.length === 0) ? (
            <div className="bg-card border border-border rounded-lg p-6 text-center text-sm text-muted-foreground">
              Aún no hay contenedores asignados a este embarque.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.containers.map((c) => <ContainerCard key={c.container_number} container={c} />)}
            </div>
          )}
        </section>

        {/* Timeline */}
        <section className="space-y-3">
          <h2 className="text-lg font-heading flex items-center gap-2">
            <Clock size={18} />
            Línea de tiempo ({data.events?.length || 0})
          </h2>
          {(!data.events || data.events.length === 0) ? (
            <div className="bg-card border border-border rounded-lg p-6 text-center text-sm text-muted-foreground">
              Aún no hay eventos registrados.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-5">
              {data.events.map((e, i) => (
                <TimelineEntry key={i} event={e} isLast={i === data.events.length - 1} />
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-border py-6 mt-10">
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <img src="/logo-ofimex-white.png" alt="OFIMEX" className="h-5 w-auto opacity-70" />
            <span>Powered by OFIMEX · myofimex.com</span>
          </div>
          <div>Este link es generado por tu agente de logística. No comparta credenciales.</div>
        </div>
      </footer>
    </div>
  );
}
